import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Loader2, GitMerge, RefreshCw, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Admin tool: reps stored under multiple user_ids across estimates → rewrite to the current account id. */
export function LeadIdentityMergeCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/lead-identities`, { withCredentials: true });
      setData(r.data);
    } catch (e) { setData({ groups: [], error: e?.response?.data?.detail || 'Could not scan lead identities' }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const merge = async (g) => {
    const key = `${g.field}:${g.email || g.name}`;
    setBusy(key);
    try {
      const r = await axios.post(`${API_URL}/api/admin/lead-identities/merge`,
        { field: g.field, target_user_id: g.account.user_id, stale_ids: g.stale_ids }, { withCredentials: true });
      toast.success(`Merged ${g.name || g.email} — ${r.data.estimates_updated} estimate(s) now point at the current account`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); } finally { setBusy(null); }
  };

  const groups = data?.groups || [];
  return (
    <Card className="border-slate-200" data-testid="lead-identity-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-[#4ABFCF]" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Sales / CS lead identities</h3>
          {data && !data.error && (
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${groups.length ? 'bg-amber-500 text-white' : 'bg-emerald-100 text-emerald-700'}`} data-testid="lead-identity-count">{groups.length}</span>
          )}
          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs text-slate-500" onClick={load} data-testid="lead-identity-refresh-btn"><RefreshCw className="w-3 h-3 mr-1" /> Rescan</Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Reps assigned to estimates under more than one id (e.g. an old sign-in id and the current account) appear twice in pipeline views. Merging rewrites those assignments to the current account id.</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">{data.error}</p>}
        {data && !data.error && groups.length === 0 && (
          <p className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="lead-identity-clean"><CheckCircle2 className="w-3.5 h-3.5" /> Every lead assignment points at a single current account.</p>
        )}
        {groups.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {groups.map((g) => {
              const key = `${g.field}:${g.email || g.name}`;
              return (
                <li key={key} className="py-2.5 flex flex-col md:flex-row md:items-center gap-2" data-testid={`lead-identity-group-${g.field}-${(g.email || g.name || '').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="font-semibold text-[#0F2040]">{g.name || g.email} <span className="font-normal text-slate-400">· {g.field === 'sales_lead' ? 'Sales lead' : 'CS lead'} · {g.estimates} estimate(s)</span></p>
                    <p className="text-slate-500 truncate">
                      ids: {g.ids.map((i) => `${i.user_id.slice(0, 14)}${i.user_id.length > 14 ? '…' : ''} (${i.count})`).join(' · ')}
                    </p>
                    <p className="text-slate-500">
                      {g.account ? <>current account: <span className="font-mono">{g.account.user_id.slice(0, 14)}…</span> ({g.account.email})</> : <span className="text-rose-600">no matching user account found — assign manually</span>}
                    </p>
                  </div>
                  <Button size="sm" className="h-7 text-xs bg-[#0F2040] hover:bg-[#1a2f5a] text-white shrink-0" disabled={!g.mergeable || busy === key} onClick={() => merge(g)} data-testid={`lead-identity-merge-btn-${g.field}-${(g.email || g.name || '').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                    {busy === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><GitMerge className="w-3 h-3 mr-1" /> Merge {g.stale_ids.length} stale id{g.stale_ids.length === 1 ? '' : 's'}</>}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
